import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/stores/hook";
import { listAIModels } from "../api/ai.api";
import { setCurrentModelId } from "../stores/aiChatSlice";

// ---------------------------------------------------------------------------
// ModelPicker — small dropdown at the panel header. Reads enabled models
// from /ai/models and stamps the first one as default on mount if none is
// selected yet.
// ---------------------------------------------------------------------------

export default function ModelPicker() {
  const dispatch = useAppDispatch();
  const currentModelId = useAppSelector((s) => s.aiChat.currentModelId);

  const { data: models = [], isLoading } = useQuery({
    queryKey: ["ai-models"],
    queryFn: listAIModels,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!currentModelId && models.length > 0) {
      dispatch(setCurrentModelId(models[0].id));
    }
  }, [currentModelId, models, dispatch]);

  if (isLoading) {
    return <span style={{ fontSize: 12, opacity: 0.6 }}>Loading models…</span>;
  }
  if (!models.length) {
    return (
      <span style={{ fontSize: 12, opacity: 0.7 }}>
        No models configured
      </span>
    );
  }

  return (
    <select
      value={currentModelId ?? ""}
      onChange={(e) => dispatch(setCurrentModelId(Number(e.target.value)))}
      style={{
        fontSize: 12,
        padding: "4px 8px",
        borderRadius: 6,
        border: "1px solid var(--color-black-3, #e5e7eb)",
        background: "var(--color-bg, #fff)",
        // Fills whatever the header toolbar leaves it and truncates the model
        // name natively instead of pushing the voice controls off the panel.
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
      }}
      aria-label="AI model"
    >
      {models.map((m) => (
        <option key={m.id} value={m.id}>
          {m.displayName}
        </option>
      ))}
    </select>
  );
}
